import React, { useState, useEffect, useMemo } from "react";
import "./Portfolio.css";
import MainLayout from "../../components/MainLayout/MainLayout";
import Images from "../../constants/Images";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGithub,
  faReact,
  faNode,
  faJs,
} from "@fortawesome/free-brands-svg-icons";
import {
  faFilter,
  faCode,
  faGlobe,
  faDatabase,
  faServer,
} from "@fortawesome/free-solid-svg-icons";
import { useInView } from "react-intersection-observer";

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [animateCard, setAnimateCard] = useState({ y: 0, opacity: 1 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const projectVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  // Project data with added categories
  const projectsArray = useMemo(() => [
    {
      id: 1,
      title: "Sahii Blog App",
      description:
        "A full-stack MERN application, empowering global sharing with robust authentication and a user-centric interface.",
      image: Images.Sahii,
      category: ["web", "fullstack", "react"],
      tags: ["React", "MongoDB", "Express", "Node.js", "TailwindCSS"],
      liveUrl: "",
      githubUrl: "https://github.com/Mo-Gamer-2000/Sahii-Blog-App",
      icons: [faReact, faNode, faDatabase],
    },
    {
      id: 2,
      title: "Convo-GPT",
      description:
        "AI Chat Bot with TypeScript, MongoDB, and OpenAI integration featuring user authentication and a sleek React interface.",
      image: Images.ConvoGPT,
      category: ["web", "ai", "react"],
      tags: ["TypeScript", "OpenAI", "MongoDB", "React"],
      liveUrl: "",
      githubUrl: "https://github.com/Mo-Gamer-2000/Convo-GPT",
      icons: [faReact, faDatabase, faCode],
    },
    {
      id: 3,
      title: "Appointify",
      description:
        "A web application built using ASP .NET Core MVC for scheduling and managing appointments with secure authentication.",
      image: Images.Appointify,
      category: ["web", "fullstack", "dotnet"],
      tags: ["C#", "ASP.NET", "SQL", "Bootstrap"],
      liveUrl: "",
      githubUrl: "https://github.com/Mo-Gamer-2000/AppointmentManagementSystem",
      icons: [faCode, faDatabase, faServer],
    },
    {
      id: 4,
      title: "BlogBlast",
      description:
        "A secure digital blogging platform built with Razor SSR .NET 8, C#, and SQL Server SSMS offering user authentication and blog management.",
      image: Images.BlogBlast,
      category: ["web", "fullstack", "dotnet"],
      tags: ["C#", ".NET 8", "SQL Server", "Razor SSR"],
      liveUrl: "",
      githubUrl: "https://github.com/Mo-Gamer-2000/BlogBlastApp",
      icons: [faCode, faDatabase, faServer],
    },
    {
      id: 5,
      title: "Coming Soon",
      description: "Stay tuned for our upcoming project!",
      image: Images.ComingSoon,
      category: ["upcoming"],
      tags: ["Future", "Project"],
      liveUrl: "",
      githubUrl: "#",
      icons: [faCode],
    },
  ], []);

  // Intersection observer for section animations
  const [portfolioHeaderRef, portfolioHeaderInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [portfolioFilterRef, portfolioFilterInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Handle filter changes
  const handleFilterClick = (filter) => {
    setActiveFilter(filter);

    // Apply filter animation
    setAnimateCard({ y: 100, opacity: 0 });

    setTimeout(() => {
      setAnimateCard({ y: 0, opacity: 1 });

      if (filter === "all") {
        setFilteredProjects(projectsArray);
      } else {
        setFilteredProjects(
          projectsArray.filter((project) => project.category.includes(filter))
        );
      }
    }, 500);
  };

  // Initialize filtered projects
  useEffect(() => {
    setFilteredProjects(projectsArray);
  }, [projectsArray]);

  // Get icon component for a tag
  const getTagIcon = (tag) => {
    switch (tag.toLowerCase()) {
      case "react":
        return <FontAwesomeIcon icon={faReact} />;
      case "node.js":
        return <FontAwesomeIcon icon={faNode} />;
      case "javascript":
        return <FontAwesomeIcon icon={faJs} />;
      default:
        return null;
    }
  };

  return (
    <MainLayout>
      <motion.div
        className="portfolio-wrapper"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Portfolio Header */}
        <section className="portfolio-header-section" ref={portfolioHeaderRef}>
          <div className="portfolio-container">
            <motion.div
              className="portfolio-header"
              initial={{ opacity: 0, y: 30 }}
              animate={
                portfolioHeaderInView
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: 30 }
              }
              transition={{ duration: 0.8 }}
            >
              <h1 className="portfolio-title">My Projects</h1>
              <div className="portfolio-title-underline"></div>
              <motion.p
                className="portfolio-subtitle"
                initial={{ opacity: 0 }}
                animate={
                  portfolioHeaderInView ? { opacity: 1 } : { opacity: 0 }
                }
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Showcasing my latest work and technical expertise
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Portfolio Filters */}
        <section className="portfolio-filter-section" ref={portfolioFilterRef}>
          <div className="portfolio-container">
            <motion.div
              className="portfolio-filter-container"
              initial={{ opacity: 0, y: 20 }}
              animate={
                portfolioFilterInView
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: 20 }
              }
              transition={{ duration: 0.6 }}
            >
              <div className="portfolio-filter-icon">
                <FontAwesomeIcon icon={faFilter} />
              </div>
              <div className="portfolio-filter-buttons">
                {[
                  "all",
                  "web",
                  "fullstack",
                  "react",
                  "dotnet",
                  "ai",
                  "upcoming",
                ].map((filter, index) => (
                  <motion.button
                    key={index}
                    className={`portfolio-filter-btn ${
                      activeFilter === filter ? "active" : ""
                    }`}
                    whileHover={{ y: -3 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => handleFilterClick(filter)}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Portfolio Projects */}
        <section className="portfolio-projects-section">
          <div className="portfolio-container">
            <motion.div
              className="portfolio-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {filteredProjects.map((project) => (
                <motion.div
                  className="portfolio-card"
                  key={project.id}
                  variants={projectVariants}
                  animate={animateCard}
                  whileHover={{
                    y: -10,
                    boxShadow: "0 15px 35px rgba(0, 0, 0, 0.15)",
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="portfolio-card-image">
                    <img src={project.image} alt={project.title} />
                    <div className="portfolio-overlay">
                      <div className="portfolio-overlay-content">
                        <div className="portfolio-icon-row">
                          {project.icons.map((icon, index) => (
                            <div key={index} className="portfolio-tech-icon">
                              <FontAwesomeIcon icon={icon} />
                            </div>
                          ))}
                        </div>
                        <div className="portfolio-buttons">
                          {project.githubUrl !== "#" && (
                            <a
                              href={project.githubUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="portfolio-btn portfolio-github-btn"
                            >
                              <FontAwesomeIcon icon={faGithub} /> GitHub
                            </a>
                          )}
                          {project.liveUrl && (
                            <a
                              href={project.liveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="portfolio-btn portfolio-live-btn"
                            >
                              <FontAwesomeIcon icon={faGlobe} /> Live Demo
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="portfolio-card-content">
                    <h3 className="portfolio-card-title">{project.title}</h3>
                    <p className="portfolio-card-desc">{project.description}</p>
                    <div className="portfolio-tags">
                      {project.tags.map((tag, index) => (
                        <span key={index} className="portfolio-tag">
                          {getTagIcon(tag)} {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </motion.div>
    </MainLayout>
  );
};

export default Portfolio;
